import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Alert,
  Button,
  Drawer,
  Popover,
  Spin,
  Table,
  Tooltip,
  message,
} from 'antd';
// eslint-disable-next-line import/no-extraneous-dependencies
import { InfoCircleOutlined } from '@ant-design/icons';
import {
  getAutoUploadCategories,
  updateAutoUploadCategoryLines,
} from '../../../actions/categories';
import { captureAnalytics } from '../../../actions/preload';
import {
  updateEmrAutoUploadCategorySettings,
} from '../../../actions/userProfile';
import EmrAutoUploadCategorySettings from './EmrAutoUploadCategorySettings';
import './auto-upload-categories.scss';

// Wide enough that the panel reaches well left of the content, with a real gutter
// on both sides — the cards were sitting almost on the panel edge.
const DRAWER_WIDTH = 600;

// Rounded on the leading edge only: the panel is anchored right, so the outer edge
// stays flush while the edge that sits over the page reads as a card. Applied to the
// content wrapper, which is the element that clips.
const DRAWER_WRAPPER_STYLE = {
  borderRadius: '18px 0 0 18px',
  overflow: 'hidden',
};

const DRAWER_HEADER_STYLE = {
  padding: '20px 40px',
  borderBottom: 0,
};

// The panel's gutter is owned by `.auto-upload__drawer` (this component's own
// markup) rather than antd's body padding. Every attempt to set it through antd —
// a descendant rule, then bodyStyle — left the intro copy and the footer sitting on
// the panel edge, so the padding now lives on an element whose styling is entirely
// ours and cannot be undone by antd's internals.
const DRAWER_BODY_STYLE = { padding: 0 };

const ORGANIZATION_GROUP = 'Organization-wide';

// A fax number owned by the organization rather than a department is grouped last
// under a label that says what it is, so a bare phone number never has to be
// recognised on sight.
const groupLabel = (faxNumber) => (
  faxNumber.owner_type === 'Organization' || !faxNumber.owner_name
    ? ORGANIZATION_GROUP
    : faxNumber.owner_name
);

const isDepartmentGroup = (label) => label !== ORGANIZATION_GROUP;

const countLabel = (count) => `${count} ${count === 1 ? 'fax number' : 'fax numbers'}`;

// Clearing every checkbox in the panel only turns auto-upload off everywhere when the
// panel showed every mapping the category has. The update endpoint deletes only the
// mappings whose fax number is inside the caller's visible scope
// (`Api::V1::AutoUploadCategoriesController#apply_fax_number_ids` intersects the
// existing rows with `visible_fax_number_ids` before destroying any), so a department
// admin's empty save leaves `hidden_line_count` mappings in departments they cannot see
// still auto-uploading. Saying "turned off" there would be a straight lie.
const hasHiddenMappings = (category) => (category.hidden_line_count || 0) > 0;

// The ids of the lines a bulk action may touch, collected in a single pass rather
// than filtering and then mapping over the same list twice.
const eligibleIds = (faxNumbers) => faxNumbers.flatMap((fax) => (fax.eligible ? [fax.id] : []));

const groupFaxNumbers = (faxNumbers) => {
  const groups = new Map();
  faxNumbers.forEach((faxNumber) => {
    const label = groupLabel(faxNumber);
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label).push(faxNumber);
  });

  return [...groups.entries()]
    .sort(([a], [b]) => {
      if (a === ORGANIZATION_GROUP) return 1;
      if (b === ORGANIZATION_GROUP) return -1;
      return a.localeCompare(b);
    })
    .map(([label, grouped]) => ({ label, faxNumbers: grouped }));
};

// Popover body for a status pill: the fax numbers a category actually uploads on,
// grouped by department. Exported so the Categories table's Auto-upload column can
// show the same thing without duplicating the layout.
export const FaxNumberSummary = ({ category, faxNumbers }) => {
  const byId = new Map(faxNumbers.map((fax) => [fax.id, fax]));
  const inertIds = new Set(category.inert_fax_number_ids);
  const configured = category.fax_number_ids.flatMap((id) => {
    const fax = byId.get(id);
    return fax ? [fax] : [];
  });

  return (
    <div className="auto-upload__summary">
      {groupFaxNumbers(configured).map((group) => (
        <div className="auto-upload__summary-group" key={group.label}>
          <div className="auto-upload__summary-dept">
            <span className="auto-upload__group-kind">
              {isDepartmentGroup(group.label) ? 'Department' : 'No department'}
            </span>
            {group.label}
          </div>
          {group.faxNumbers.map((fax) => (
            <div className="auto-upload__summary-fax" key={fax.id}>
              <span>{fax.number}</span>
              {inertIds.has(fax.id) && (
                <span className="auto-upload__summary-off">auto-upload switched off</span>
              )}
            </div>
          ))}
        </div>
      ))}
      {category.hidden_line_count > 0 && (
        <div className="auto-upload__summary-hidden">
          {`+${countLabel(category.hidden_line_count)} in departments you don't have access to`}
        </div>
      )}
    </div>
  );
};

FaxNumberSummary.propTypes = {
  category: PropTypes.shape({
    fax_number_ids: PropTypes.array.isRequired,
    inert_fax_number_ids: PropTypes.array,
    hidden_line_count: PropTypes.number,
  }).isRequired,
  faxNumbers: PropTypes.array.isRequired,
};

// The "Fax numbers uploading it" cell: one chip per mapped fax number, plus a single
// chip standing in for the departments the caller cannot see.
const FaxNumberChips = ({ category, linesById }) => {
  const inertIds = new Set(category.inert_fax_number_ids);

  if (category.fax_number_ids.length === 0 && category.hidden_line_count === 0) {
    return (
      <span className="auto-upload__muted">
        No fax numbers — documents stay in Medsender for manual review
      </span>
    );
  }

  return (
    <div className="auto-upload__chips">
      {category.fax_number_ids.map((id) => {
        const line = linesById.get(id);
        if (!line) return null;
        const inert = inertIds.has(id);
        return (
          <span
            key={id}
            className={`auto-upload__chip${inert ? ' auto-upload__chip--inert' : ''}`}
          >
            {`${groupLabel(line)} · ${line.number}`}
            {inert && <span className="auto-upload__chip-note">switched off</span>}
          </span>
        );
      })}
      {category.hidden_line_count > 0 && (
        <Tooltip title="These fax numbers belong to departments you don't have access to. An administrator can see them.">
          <span className="auto-upload__chip auto-upload__chip--hidden">
            {`+${countLabel(category.hidden_line_count)} in other departments`}
          </span>
        </Tooltip>
      )}
    </div>
  );
};

FaxNumberChips.propTypes = {
  category: PropTypes.object.isRequired,
  linesById: PropTypes.instanceOf(Map).isRequired,
};

// The "Category" cell: the EMR category name and the pills that say whether it
// actually uploads anything. "Configured, off" and "On" are mutually exclusive — a
// category whose every mapping is switched off uploads nothing, so claiming "On"
// would be wrong.
const CategoryStatusCell = ({ category, lines }) => {
  const on = category.fax_number_ids.length > 0 || category.hidden_line_count > 0;
  const allInert = on
    && category.hidden_line_count === 0
    && category.inert_fax_number_ids.length === category.fax_number_ids.length;

  return (
    <div className="auto-upload__row-name">
      <span className={`auto-upload__name${on ? '' : ' auto-upload__name--off'}`}>
        {category.emr_category}
      </span>
      {on && !allInert && (
        <Popover
          trigger="click"
          placement="right"
          title="Uploads on these fax numbers"
          content={<FaxNumberSummary category={category} faxNumbers={lines} />}
        >
          <button type="button" className="auto-upload__pill auto-upload__pill--on auto-upload__pill--clickable">
            On
          </button>
        </Popover>
      )}
      {allInert && (
        <Tooltip title="This category is configured, but auto-upload is switched off on every fax number it's mapped to, so nothing uploads.">
          <span className="auto-upload__pill auto-upload__pill--warn">Configured, off</span>
        </Tooltip>
      )}
      {!category.enabled && (
        <Tooltip title="This category is turned off in the Categories tab, so the AI never assigns it.">
          <span className="auto-upload__pill">Category off</span>
        </Tooltip>
      )}
    </div>
  );
};

CategoryStatusCell.propTypes = {
  category: PropTypes.object.isRequired,
  lines: PropTypes.array.isRequired,
};

// One department's block of checkboxes in the editor panel, with its bulk control.
const FaxNumberGroup = ({
  group, draftIdSet, savedIdSet, canEdit, onToggle, onSelectAll, onClear,
}) => {
  // Both sides of this comparison must count the same population. Counting every
  // selected line against the eligible-only total let a selected ineligible mapping
  // stand in for an unselected eligible fax number, so the button offered "Clear all"
  // while some eligible ones were still off.
  const addable = group.faxNumbers.filter((fax) => fax.eligible);
  const groupSelected = addable.filter((fax) => draftIdSet.has(fax.id));
  const allSelected = addable.length > 0 && groupSelected.length === addable.length;

  return (
    <div className="auto-upload__group">
      <div className="auto-upload__group-head">
        <span className="auto-upload__group-name">
          <span className="auto-upload__group-kind">
            {isDepartmentGroup(group.label) ? 'Department' : 'No department'}
          </span>
          {group.label}
        </span>
        {canEdit && addable.length > 1 && (
          <button
            type="button"
            className="auto-upload__select-all"
            onClick={() => (
              allSelected
                ? onClear(group.faxNumbers)
                : onSelectAll(group.faxNumbers)
            )}
          >
            {allSelected ? 'Clear all' : `Select all ${addable.length}`}
          </button>
        )}
      </div>

      {group.faxNumbers.map((fax) => {
        const checked = draftIdSet.has(fax.id);
        // An ineligible fax number can be unchecked but never newly checked: inactive
        // and user-owned numbers are not offered, yet an existing mapping on one must
        // stay visible and removable.
        const locked = !canEdit || (!fax.eligible && !checked);
        let state = null;
        if (checked && !savedIdSet.has(fax.id)) state = 'adding';
        else if (checked) state = 'already on';
        else if (savedIdSet.has(fax.id)) state = 'removing';

        const rowClass = [
          'auto-upload__fax',
          checked ? 'auto-upload__fax--checked' : '',
          locked ? 'auto-upload__fax--locked' : '',
        ].filter(Boolean).join(' ');

        return (
          <label
            className={rowClass}
            key={fax.id}
            htmlFor={`auto-upload-fax-${fax.id}`}
          >
            <input
              id={`auto-upload-fax-${fax.id}`}
              type="checkbox"
              checked={checked}
              disabled={locked}
              onChange={() => onToggle(fax.id)}
            />
            <span className="auto-upload__fax-number">{fax.number}</span>
            <span className="auto-upload__fax-meta">
              {!fax.active && 'inactive'}
              {fax.active && state}
            </span>
          </label>
        );
      })}
    </div>
  );
};

FaxNumberGroup.propTypes = {
  group: PropTypes.object.isRequired,
  draftIdSet: PropTypes.instanceOf(Set).isRequired,
  savedIdSet: PropTypes.instanceOf(Set).isRequired,
  canEdit: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};

FaxNumberGroup.defaultProps = { canEdit: false };

// The editor panel's contents. Rendered only while a category is being edited, so
// `category` is never null here and the draft/saved comparison never has to guard
// for it.
const AutoUploadDrawerBody = ({
  category, lines, draftIds, canEdit, saving, onToggle, onSelectAll, onClear, onCancel, onSave,
}) => {
  const draftIdSet = new Set(draftIds);
  const savedIdSet = new Set(category.fax_number_ids);
  const draftAdded = draftIds.filter((id) => !savedIdSet.has(id));
  const draftRemoved = category.fax_number_ids.filter((id) => !draftIdSet.has(id));
  const draftKept = draftIds.filter((id) => savedIdSet.has(id));
  const dirty = draftAdded.length > 0 || draftRemoved.length > 0;
  const hidden = hasHiddenMappings(category);

  let emptyEffect = `No fax numbers selected — ${category.emr_category} documents will stay in Medsender for manual review.`;
  let emptySaveLabel = 'Turn off auto-upload';
  if (hidden) {
    emptyEffect = `No fax numbers selected — ${category.emr_category} documents arriving on the fax numbers above will stay in Medsender for manual review. This category is also configured on ${countLabel(category.hidden_line_count)} in departments you don't have access to, and those keep uploading automatically.`;
    emptySaveLabel = 'Turn off for these fax numbers';
  }

  return (
    <div className="auto-upload__drawer">
      <p className="auto-upload__drawer-help">
        Uploads as
        {' '}
        <code>{category.emr_category}</code>
        {' '}
        in your EMR. Pick the department fax numbers that should upload this category
        automatically.
      </p>

      {groupFaxNumbers(lines).map((group) => (
        <FaxNumberGroup
          key={group.label}
          group={group}
          draftIdSet={draftIdSet}
          savedIdSet={savedIdSet}
          canEdit={canEdit}
          onToggle={onToggle}
          onSelectAll={onSelectAll}
          onClear={onClear}
        />
      ))}

      <div className="auto-upload__effect">
        <strong>What this will do</strong>
        {draftIds.length === 0
          ? emptyEffect
          : `${category.emr_category} documents arriving on the ${countLabel(draftIds.length)} you selected will file themselves into the patient's chart. Both halves get configured together: each fax number is switched on for auto-upload and mapped to this category, and a document only uploads when both are true. Switching a fax number on also stops its "fax received" email notifications.`}
      </div>

      <div className="auto-upload__drawer-actions">
        <span className="auto-upload__delta">
          Adding
          {' '}
          <strong>{draftAdded.length}</strong>
          {' · keeping '}
          <strong>{draftKept.length}</strong>
          {' · removing '}
          <strong>{draftRemoved.length}</strong>
        </span>
        <span>
          <Button onClick={onCancel} style={{ marginRight: 8 }}>Cancel</Button>
          <Button
            type="primary"
            loading={saving}
            disabled={!dirty}
            onClick={onSave}
          >
            {draftIds.length === 0 ? emptySaveLabel : `Save ${countLabel(draftIds.length)}`}
          </Button>
        </span>
      </div>
    </div>
  );
};

AutoUploadDrawerBody.propTypes = {
  category: PropTypes.object.isRequired,
  lines: PropTypes.array.isRequired,
  draftIds: PropTypes.array.isRequired,
  canEdit: PropTypes.bool,
  saving: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

AutoUploadDrawerBody.defaultProps = { canEdit: false, saving: false };

// The whole pane for an organization with an EmrIntegrationCredential. Such an org
// never reads the per-fax mappings: PatientInformationExtractionWorker routes it to
// RunThreeLayerAutoUploadWorker, which gates on the credential's own allow-list.
// Showing it a per-line picker would be showing it a control that does nothing, so
// this pane hosts the allow-list editor instead — one tab, whichever editor the
// organization's integration actually uses. Only the category half moves;
// duplicate-upload enforcement stays in Settings → Features, where it belongs.
// Split out of the page component because its gating is the bulk of the branch and
// has nothing to do with the per-fax table, drawer or draft state below it.
const IntegrationManagedPane = ({ canEdit, credentialSettings, saveCredentialSettings }) => {
  // What the pane may say when the editor is closed. Only the credential's stored
  // settings decide what uploads — RunThreeLayerAutoUploadWorker gates on
  // EmrIntegrationCredential#auto_upload_category_allowed?, which never looks at
  // three_layer_auto_upload_categories — so an allow-list saved while the flag was on
  // is still enforced after it is turned off. Claiming "every category uploads"
  // whenever the editor is hidden would therefore tell an admin the opposite of what
  // the worker does. The whitelist is reported read-only instead (null = unrestricted,
  // [] = nothing uploads, list = only those).
  const categorySettingsEnabled = credentialSettings.can_configure_categories === true;
  const restrictedTo = credentialSettings.enabled === true || credentialSettings.enabled === 'true'
    ? (credentialSettings.categories || [])
    : null;
  const readOnlyRestriction = () => {
    if (restrictedTo.length === 0) {
      return (
        <Alert
          type="warning"
          showIcon
          message="No document categories upload automatically"
          description={
            'Your EMR integration is restricted to an empty list of categories, so every '
            + 'document stays in Medsender for manual review. This list cannot be edited '
            + 'here — contact Medsender support to change it.'
          }
        />
      );
    }

    return (
      <Alert
        type="info"
        showIcon
        message={`Auto-upload is limited to ${restrictedTo.length} document ${restrictedTo.length === 1 ? 'category' : 'categories'}`}
        description={(
          <>
            <div>
              Only these categories upload to your EMR automatically; everything else stays
              in Medsender for manual review.
            </div>
            <div className="auto-upload__readonly-list">
              {restrictedTo.map((category) => (
                <span className="auto-upload__chip" key={category}>{category}</span>
              ))}
            </div>
            <div>
              This list cannot be edited here — contact Medsender support to change it.
            </div>
          </>
        )}
      />
    );
  };

  // Everything below the heading. The editor is only safe to show once we know the
  // org may actually use it AND its current allow-list has arrived — rendering it any
  // earlier hands the admin a form whose Save would overwrite settings it never
  // loaded.
  const renderCredentialEditor = () => {
    // The endpoint gates category writes on three_layer_auto_upload_categories and
    // silently no-ops without it, so offering the editor here would produce a
    // "saved successfully" toast over a change that never happened.
    if (!categorySettingsEnabled) {
      if (restrictedTo) return readOnlyRestriction();

      return (
        <Alert
          type="info"
          showIcon
          message="Your EMR integration uploads every document category"
          description={
            'This organization auto-uploads through its EMR integration without a '
            + 'category restriction. Contact Medsender support to limit which '
            + 'categories upload automatically.'
          }
        />
      );
    }

    if (!canEdit) {
      return (
        <Alert
          type="info"
          showIcon
          message="Only administrators can change auto-upload."
        />
      );
    }

    return (
      <EmrAutoUploadCategorySettings
        settings={credentialSettings || {}}
        updateSettings={saveCredentialSettings}
        canConfigureCategories
        canConfigureUploadDedup={false}
      />
    );
  };

  return (
    <div className="auto-upload">
      <div className="auto-upload__header">
        <h2 className="auto-upload__title">Auto-upload to your EMR</h2>
        {categorySettingsEnabled && (
          <div className="auto-upload__subtitle">
            Your EMR integration decides this for the whole organization rather than per
            fax number: documents in the categories you choose here upload automatically,
            and everything else stays in Medsender for manual review.
          </div>
        )}
      </div>
      {renderCredentialEditor()}
    </div>
  );
};

IntegrationManagedPane.propTypes = {
  canEdit: PropTypes.bool,
  credentialSettings: PropTypes.object,
  saveCredentialSettings: PropTypes.func.isRequired,
};

IntegrationManagedPane.defaultProps = {
  canEdit: false,
  credentialSettings: {},
};

const AutoUploadCategories = ({
  autoUpload, fetchAutoUploadCategories, saveCategoryLines,
  credentialSettings, saveCredentialSettings, mode, isAdmin,
}) => {
  const [editing, setEditing] = useState(null);
  const [draftIds, setDraftIds] = useState([]);
  const [saving, setSaving] = useState(false);

  const {
    lines, categories, canEdit, orgWideAutoUpload, isLoading, loaded, error,
  } = autoUpload;

  // Refetch every time the pane opens rather than only when the slice is empty.
  // ManageCategories loads this once on mount for the Categories table's status
  // column, but categories can be added, renamed, given an EMR category or deleted
  // in that table afterwards — and this pane only remounts when the sub-tab is
  // opened, so "already loaded" is not the same as "still true". Skipping the fetch
  // left the empty state telling users to add a category first and then not showing
  // the one they just added. `loaded` survives a refetch, so nothing blanks out.
  useEffect(() => {
    if (mode === 'per-fax') fetchAutoUploadCategories();
  }, [mode, fetchAutoUploadCategories]);

  const linesById = useMemo(
    () => new Map(lines.map((line) => [line.id, line])),
    [lines],
  );

  // Counts, ids and booleans only. A fax number is a contact identifier, so the
  // numbers themselves never leave the component — the product questions ("do admins
  // open this editor?", "how many fax numbers do they map?") only need magnitudes.
  const openDrawer = (category) => {
    captureAnalytics('auto_upload_category_editor_opened', {
      document_category_id: category.document_category_id,
      category_enabled: category.enabled === true,
      mapped_fax_number_count: category.fax_number_ids.length,
      hidden_fax_number_count: category.hidden_line_count || 0,
      inert_fax_number_count: (category.inert_fax_number_ids || []).length,
      visible_fax_number_count: lines.length,
    });
    setEditing(category);
    setDraftIds([...category.fax_number_ids]);
  };

  const closeDrawer = () => {
    setEditing(null);
    setDraftIds([]);
  };

  const toggleLine = (lineId) => {
    setDraftIds((previous) => (
      previous.includes(lineId)
        ? previous.filter((id) => id !== lineId)
        : [...previous, lineId]
    ));
  };

  // "Select all" only ever adds, and only lines that may be newly mapped —
  // an ineligible line already mapped stays as it is rather than being cleared.
  const selectAllInGroup = (groupedLines) => {
    setDraftIds((previous) => [...new Set([...previous, ...eligibleIds(groupedLines)])]);
  };

  // The mirror of "Select all", and deliberately as narrow: it only ever clears the
  // lines "Select all" could have added. Clearing an ineligible line's existing
  // mapping would strand it — once unchecked it becomes locked and cannot be
  // re-checked, so a stray bulk click could only be undone by discarding the whole
  // drawer. Removing one of those stays a per-line decision.
  const clearGroup = (groupedLines) => {
    const ids = new Set(eligibleIds(groupedLines));
    setDraftIds((previous) => previous.filter((id) => !ids.has(id)));
  };

  // Both outcomes emit the same event with the same shape, so a failure count has a
  // denominator. Same PHI rule as the open event: magnitudes, not fax numbers.
  const trackSave = (succeeded, category, selectedIds) => {
    const savedIdSet = new Set(category.fax_number_ids);
    const draftIdSet = new Set(selectedIds);
    captureAnalytics('auto_upload_category_saved', {
      succeeded,
      document_category_id: category.document_category_id,
      selected_fax_number_count: selectedIds.length,
      added_fax_number_count: selectedIds.filter((id) => !savedIdSet.has(id)).length,
      removed_fax_number_count: category.fax_number_ids.filter((id) => !draftIdSet.has(id)).length,
      hidden_fax_number_count: category.hidden_line_count || 0,
      // True only when the save really leaves the category uploading nowhere — an
      // empty selection with hidden mappings behind it does not.
      turned_off_everywhere: selectedIds.length === 0 && !hasHiddenMappings(category),
    });
  };

  const handleSave = async () => {
    // The drawer can be closed while the request is pending. Keep the category and
    // selection that this save started with instead of reading state that may have
    // been cleared by closeDrawer before the response arrives.
    const category = editing;
    const selectedIds = [...draftIds];
    setSaving(true);
    let response;
    try {
      response = await saveCategoryLines(category.document_category_id, selectedIds);
    } catch {
      // A rejected request (rather than one that resolves with an error payload)
      // used to escape as an unhandled rejection: the flag stayed set, Save span
      // forever, and the user was told nothing.
      message.error('Failed to save auto-upload settings. Please try again.');
      trackSave(false, category, selectedIds);
      return;
    } finally {
      setSaving(false);
    }

    if (response?.error) {
      const errorMessage = response.response?.data?.error
        || response.response?.data?.message
        || 'Failed to save auto-upload settings';
      message.error(errorMessage);
      trackSave(false, category, selectedIds);
      return;
    }

    trackSave(true, category, selectedIds);
    // The empty-selection wording has to match what the endpoint actually did: it only
    // removed the mappings on fax numbers this caller can see, so with hidden mappings
    // left behind the category is still auto-uploading elsewhere.
    let successMessage = `${category.emr_category} will auto-upload on ${countLabel(selectedIds.length)}`;
    if (selectedIds.length === 0 && hasHiddenMappings(category)) {
      successMessage = `Auto-upload turned off for ${category.emr_category} on your fax numbers. It stays on for ${countLabel(category.hidden_line_count)} in departments you don't have access to.`;
    } else if (selectedIds.length === 0) {
      successMessage = `Auto-upload turned off for ${category.emr_category}`;
    }
    message.success(successMessage);
    closeDrawer();
  };

  const columns = [
    {
      title: 'Category',
      key: 'category',
      render: (_, category) => <CategoryStatusCell category={category} lines={lines} />,
    },
    {
      title: 'Fax numbers uploading it',
      key: 'faxNumbers',
      render: (_, category) => <FaxNumberChips category={category} linesById={linesById} />,
    },
    {
      title: '',
      key: 'edit',
      width: 110,
      render: (_, category) => {
        const label = category.fax_number_ids.length > 0 ? 'Edit fax numbers' : 'Add fax numbers';
        if (!canEdit) {
          return <Button size="small" disabled>{label}</Button>;
        }
        return (
          <Button size="small" type="link" onClick={() => openDrawer(category)}>
            {label}
          </Button>
        );
      },
    },
  ];

  if (mode === 'loading' || (mode === 'per-fax' && isLoading && !loaded)) {
    return <Spin size="large" />;
  }

  if (mode === 'failed') {
    return (
      <Alert
        className="auto-upload__banner"
        type="error"
        showIcon
        message="We couldn't load your auto-upload configuration"
        description="Refresh the page to try again. Nothing has been changed."
      />
    );
  }

  // The credential settings response is the canonical mode signal. This endpoint
  // owns only its per-fax mappings, so credential-backed organizations never receive
  // a legacy mapping payload that the runtime will ignore.
  //
  // Authorization comes from the profile, not from `autoUpload.canEdit`: that field is
  // only ever set by the per-fax response, which this mode deliberately never requests,
  // so reading it here would leave every integration-managed admin at its initial
  // `false` and show them "Only administrators can change auto-upload" forever.
  // `state.userProfile.isAdmin` is the same predicate on the same user — the users
  // endpoint reports `current_user.is_user_admin?`, which is exactly what the per-fax
  // payload's `can_edit` and the settings endpoint's own `ensure_admin` write gate use.
  if (mode === 'integration') {
    return (
      <IntegrationManagedPane
        canEdit={isAdmin}
        credentialSettings={credentialSettings}
        saveCredentialSettings={saveCredentialSettings}
      />
    );
  }

  return (
    <div className="auto-upload">
      <div className="auto-upload__header">
        <h2 className="auto-upload__title">Auto-upload to your EMR</h2>
        <div className="auto-upload__subtitle">
          A document uploads on its own only when
          {' '}
          <strong>both</strong>
          {' '}
          halves are configured: the fax number it arrived on, and the document category the
          AI assigned to it. Selecting department fax numbers for a category below configures
          both halves together, so there is no way to end up with only one of them set.
        </div>
      </div>

      {orgWideAutoUpload && (
        <Alert
          className="auto-upload__banner"
          type="warning"
          showIcon
          message="Your organization uploads every document automatically"
          description="Every received document already files itself, so these per-fax-number settings have no effect. Contact support to change it."
        />
      )}

      {!canEdit && (
        <Alert
          className="auto-upload__banner"
          type="info"
          showIcon
          message="Only administrators can change auto-upload. You're seeing the fax numbers in your departments."
        />
      )}

      {error && (
        <Alert className="auto-upload__banner" type="error" showIcon message={error} />
      )}

      <Alert
        className="auto-upload__banner"
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        message="This does not create patient charts. It files documents into charts that already exist."
      />

      <Table
        className="auto-upload__table"
        size="middle"
        dataSource={categories}
        columns={columns}
        rowKey="document_category_id"
        pagination={false}
        locale={{
          emptyText: (
            <div className="auto-upload__empty">
              No document categories have an EMR category yet. Add one in the Categories tab
              first.
            </div>
          ),
        }}
      />

      <Drawer
        className="auto-upload-drawer"
        width={DRAWER_WIDTH}
        visible={Boolean(editing)}
        onClose={closeDrawer}
        title={editing ? editing.emr_category : ''}
        contentWrapperStyle={DRAWER_WRAPPER_STYLE}
        headerStyle={DRAWER_HEADER_STYLE}
        bodyStyle={DRAWER_BODY_STYLE}
        destroyOnClose
      >
        {editing && (
          <AutoUploadDrawerBody
            category={editing}
            lines={lines}
            draftIds={draftIds}
            canEdit={canEdit}
            saving={saving}
            onToggle={toggleLine}
            onSelectAll={selectAllInGroup}
            onClear={clearGroup}
            onCancel={closeDrawer}
            onSave={handleSave}
          />
        )}
      </Drawer>
    </div>
  );
};

AutoUploadCategories.defaultProps = {
  credentialSettings: {},
  mode: 'per-fax',
  isAdmin: false,
};

AutoUploadCategories.propTypes = {
  autoUpload: PropTypes.shape({
    lines: PropTypes.array.isRequired,
    categories: PropTypes.array.isRequired,
    canEdit: PropTypes.bool,
    orgWideAutoUpload: PropTypes.bool,
    isLoading: PropTypes.bool,
    loaded: PropTypes.bool,
    error: PropTypes.string,
  }).isRequired,
  fetchAutoUploadCategories: PropTypes.func.isRequired,
  saveCategoryLines: PropTypes.func.isRequired,
  credentialSettings: PropTypes.object,
  saveCredentialSettings: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['loading', 'per-fax', 'integration', 'failed']),
  isAdmin: PropTypes.bool,
};

export default connect(
  (state) => ({
    autoUpload: state.categories.autoUpload,
    credentialSettings: state.userProfile.emrAutoUploadCategorySettings,
    isAdmin: state.userProfile.isAdmin,
  }),
  {
    fetchAutoUploadCategories: getAutoUploadCategories,
    saveCategoryLines: updateAutoUploadCategoryLines,
    saveCredentialSettings: updateEmrAutoUploadCategorySettings,
  },
)(AutoUploadCategories);
